import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import ReactRouterPropTypes from 'react-router-prop-types';
import { isEqual, get, find, isEmpty, map } from 'lodash-es';
import { fromJS } from 'immutable';
import { withSnackbar } from 'notistack';
import { CircularProgress } from 'react-md';
import Modal from 'react-modal';
import { StepNavMenu, ExpansionPanelBlock } from '@coog/react-components';
import { SRHeader as Header } from '@coog/components-library';

import {
  partialUpdateSalesRouteAnswersTransform,
  partialUpdateSalesRouteLoansTransform,
  partialUpdateSalesRouteQuotationsTransform,
  partialUpdateSalesRouteExtraDataTransform,
  partialUpdateSalesRouteGeneralDataTransform,
  partialUpdateSalesRouteContractTransform,
  partialUpdateSalesRouteSubscriptionTransform,
} from './EditSalesRoute.transform';
import * as salesroutesActions from '../../Services/SalesRoutes/actions';
import { advancementStatusEnum } from '../../Services/SalesRoutes/enum';
import { findById, checkIsEmpty, checkSalesRouteIsFinished } from '../../Services/SalesRoutes/utils';
import { STATE_MENU } from './EditSalesRoute.enum';
import { calculateSteps, calculateHeaderState } from './config';
import { getMenu, getNextMenu } from './MenuConfig';
import './EditSalesRoute.scss';
import { showAlerts } from '../Common/utils';
import Summary from './Presentational/Summary.presentational';
import BlocksPresentational from './Presentational';

import SubscribeDialog from '../EditQuotation/SubscribeDialog';

Modal.setAppElement('#root');

class EditSalesRoute extends Component {
  constructor(props) {
    super(props);

    this.state = {
      salesroute: {
        id: undefined,
      },
      questionnaire: {
        id: undefined,
      },
      steps: [],
      hasDirtyField: {},
      willSaveForm: {},
      showModal: false,
      willResetForm: {},
      nextEditBlock: undefined,
      loading: {},
      displaySubscrDialog: false,
    };

    this.handleCancelModal = this.handleCancelModal.bind(this);
    this.handleCloseModal = this.handleCloseModal.bind(this);
    this.handleSaveModal = this.handleSaveModal.bind(this);
  }

  static getDerivedStateFromProps(nextProps, prevState) {
    let nextState = null;
    const {
      salesroute: prevSalesRoute,
      steps,
      loading: prevLoading,
      advancement: prevAdvancement,
      nextEditBlock,
      questionnaire: prevQuest,
    } = prevState;
    const {
      salesroutes,
      questionnaires,
      blockQuestionnaire,
      blockLoans,
      blockQuotation,
      blockExtraData,
      blockGeneralData,
      blockOffers,
      blockSubscription,
      blockDocuments,
      advancement,
      loading,
      strings,
    } = nextProps;

    // salesRoute currently only has one questionnaire
    let currentIdQuestionnaire = get(prevSalesRoute, 'questionnaires[0].questionnaire.id', undefined);

    // Fetch salesroute alerts
    if (!isEqual(get(nextProps, 'alerts'), get(prevState, 'alerts'))) {
      nextState = { alerts: get(nextProps, 'alerts') };
    }

    if (!isEqual(advancement?.toJS(), prevAdvancement)) {
      nextState = { ...nextState, advancement: advancement?.toJS() };
    }
    let nextSalesRouteJs;
    let newMenuConfig = steps;

    // Fetch salesroute
    try {
      if (prevSalesRoute && typeof get(prevSalesRoute, 'id') !== 'undefined' && salesroutes) {
        nextSalesRouteJs = findById({ list: salesroutes, id: get(prevSalesRoute, 'id') })?.toJS();

        // new salesRoute
        if (nextSalesRouteJs && !isEqual(nextSalesRouteJs, prevSalesRoute)) {
          const newMenu = getMenu({ questionnaire: nextSalesRouteJs.questionnaires[0] });

          // Set menu or update menu if it has changed when describe-quotations (Typically if empty blocks with no questions have been removed)
          if (isEmpty(steps) || (!isEmpty(newMenu) && steps.length !== newMenu.length)) {
            newMenuConfig = map(newMenu, step => {
              const oldStep = find(steps, { code: step.code });

              return oldStep ? { ...step, state: oldStep.state } : step;
            });

            nextState = {
              ...nextState,
              steps: newMenuConfig,
            };
          }

          // update current id questionnaire if exist new salesRoute
          nextState = {
            ...nextState,
            salesroute: nextSalesRouteJs,
          };
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getDerivedStateFromProps ${e.stack}`);
    }

    // Update steps
    const advStatus = advancement.getIn(['status']);

    if (nextSalesRouteJs) {
      currentIdQuestionnaire = get(nextSalesRouteJs, 'questionnaires[0].questionnaire.id', undefined);

      if (
        (advStatus && prevAdvancement?.status && !isEqual(advStatus, prevAdvancement?.status)) ||
        (questionnaires.size && !prevQuest?.id) // Call it once when questionnaires are loaded
      ) {
        nextState = {
          ...nextState,
          // Will update steps with nextEditBlock if there's one to be opened, or standard behavior
          steps: calculateSteps({
            advancement,
            steps: newMenuConfig,
            salesroute: nextSalesRouteJs,
            idQuestionnaire: currentIdQuestionnaire,
            nextEditBlockId: nextEditBlock,
            strings,
          }),
        };

        if (
          advStatus === advancementStatusEnum.EVENT_PATCH_SALESROUTE_SUCCESS ||
          advStatus === advancementStatusEnum.EVENT_RESET_FORM_SALESROUTE_SUCCESS
        ) {
          nextState = { ...nextState, nextEditBlock: null };
        }
      }
    }

    // Fetch related questionnaire by currentIdQuestionnaire
    try {
      if (!checkIsEmpty(currentIdQuestionnaire) && questionnaires.size > 0) {
        let questionnaireJs = findById({ list: questionnaires, id: currentIdQuestionnaire })?.toJS();

        // new questionnaire
        if (questionnaireJs) {
          nextState = { ...nextState, questionnaire: questionnaireJs };
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getDerivedStateFromProps ${e.stack}`);
    }

    // blockQuestionnaire
    try {
      if (blockQuestionnaire) {
        // new blockQuestionnaire
        nextState = { ...nextState, blockQuestionnaire: blockQuestionnaire.toJS() };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getDerivedStateFromProps blockQuestionnaire ${e.stack}`);
    }

    // blockLoans
    try {
      if (blockLoans) {
        // new blockLoans
        nextState = { ...nextState, blockLoans: blockLoans.toJS() };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getDerivedStateFromProps blockLoans ${e.stack}`);
    }

    // blockQuotation
    try {
      if (blockQuotation) {
        // new blockQuotation
        nextState = { ...nextState, blockQuotation: blockQuotation.toJS() };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getDerivedStateFromProps blockQuotation ${e.stack}`);
    }

    // blockExtraData
    try {
      if (blockExtraData) {
        // new blockExtraData
        nextState = { ...nextState, blockExtraData: blockExtraData.toJS() };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getDerivedStateFromProps blockExtraData ${e.stack}`);
    }

    // blockGeneralData
    try {
      if (blockGeneralData) {
        // new blockGeneralData
        nextState = { ...nextState, blockGeneralData: blockGeneralData.toJS() };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getDerivedStateFromProps blockGeneralData ${e.stack}`);
    }

    // Fetch related blockOffers by currentIdQuestionnaire
    try {
      if (!checkIsEmpty(currentIdQuestionnaire) && blockOffers.size > 0) {
        const blockOffersByQuestionnaire = findById({
          list: blockOffers.get('questionnaires') || fromJS([]),
          id: currentIdQuestionnaire,
        });

        if (blockOffersByQuestionnaire) {
          const blockOffersByPart = blockOffersByQuestionnaire.get('parts');
          nextState = { ...nextState, blockOffers: blockOffersByPart ? blockOffersByPart.toJS() : undefined };
        }
      } else {
        nextState = { ...nextState, blockOffers: undefined };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getDerivedStateFromProps blockOffers ${e.stack}`);
    }

    // blockSubscription
    try {
      if (blockSubscription) {
        // new blockSubscription
        nextState = { ...nextState, blockSubscription: blockSubscription.toJS() };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getDerivedStateFromPropsblockSubscription ${e.stack}`);
    }

    // blockDocuments
    try {
      if (blockDocuments) {
        // new blockDocuments
        nextState = { ...nextState, blockDocuments: blockDocuments.toJS() };
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`getDerivedStateFromProps blockDocuments ${e.stack}`);
    }

    const loadingJS = loading.toJS();

    if (!isEqual(prevLoading, loadingJS)) {
      nextState = { ...nextState, loading: loadingJS };
    }

    if (
      prevLoading.isLoadingDocumentsBlock &&
      !loading.get('isLoadingDocumentsBlock') &&
      advancement.get('status') === advancementStatusEnum.EVENT_FINALIZE_SALESROUTE_SUCCESS
    ) {
      nextState = { ...nextState, displaySubscrDialog: true };
    } else {
      nextState = { ...nextState, displaySubscrDialog: false };
    }

    return nextState;
  }

  componentDidMount() {
    const { match } = this.props;

    this.setState({
      salesroute: {
        // id salesRoutes
        id: match.params.id,
      },
      steps: [],
    });
    // this.fetchQuestionnaires();
    this.fetchSalesRoute(match.params.id);
  }

  componentDidUpdate(prevProps) {
    const { alerts: prevAlerts } = prevProps;
    const { alerts } = this.props;

    if (alerts && !isEqual(alerts.toJS(), prevAlerts.toJS())) {
      this.showAlerts();
    }
  }

  /**
   * fetch sales route
   */
  fetchSalesRoute = id => {
    const { actions } = this.props;
    actions.fetchSalesRoute({ id });
  };

  getNextAction = ({ current }) => {
    const { actions } = this.props;
    const { steps } = this.state;

    const mappingNextActions = {
      loans: actions.partialUpdateSalesRouteAnswers,
      quotations: actions.partialUpdateSalesRouteAnswers,
      quotationsLoans: actions.partialUpdateSalesRouteLoans,
      extra_data: actions.partialUpdateSalesRouteExtraData,
      generalData: actions.partialUpdateSalesRouteGeneralData,
      offers: actions.partialUpdateSalesRouteQuotations,
      print: actions.partialUpdateSalesRouteContractPrint,
      subscription: actions.partialUpdateSalesRouteContract,
      documents: actions.partialUpdateSalesRouteSubscription,
    };

    const next = current === 'print' ? current : getNextMenu({ menu: steps, current });

    return get(mappingNextActions, next);
  };

  /**
   * Partial Update SalesRoute
   */
  partialUpdateSalesRoute = type => ({ answers, saveOnly }) => () => {
    const { actions } = this.props;
    const { salesroute, questionnaire } = this.state;
    let payload;
    const codeQuestionnaire = get(questionnaire, 'code');
    const newState = { hasDirtyField: {} };
    newState.hasDirtyField[type] = false;

    switch (type) {
      case 'questionnaires':
        payload = partialUpdateSalesRouteAnswersTransform({ answers, salesroute, questionnaire });
        break;
      case 'loans':
        payload = partialUpdateSalesRouteLoansTransform({ answers, salesroute });
        break;
      case 'quotations':
        payload = partialUpdateSalesRouteQuotationsTransform({ answers, salesroute });
        break;
      case 'extra_data':
        payload = partialUpdateSalesRouteExtraDataTransform({ answers, salesroute, codeQuestionnaire });
        break;
      case 'generalData':
        payload = partialUpdateSalesRouteGeneralDataTransform({ answers, salesroute, codeQuestionnaire });
        break;
      case 'print':
        payload = partialUpdateSalesRouteContractTransform({ answers });
        newState.hasDirtyField = { offers: false };
        break;
      case 'offers':
        payload = partialUpdateSalesRouteContractTransform({ answers });
        saveOnly = false;
        break;
      case 'subscription':
        payload = partialUpdateSalesRouteSubscriptionTransform({ answers, salesroute, codeQuestionnaire });
        break;
      default:
        break;
    }

    if (!checkIsEmpty(payload)) {
      const id = get(salesroute, 'id');
      const nextAction = this.getNextAction({ current: type });

      if (saveOnly) {
        actions.patchSalesRoute({ payload, id });
      } else if (nextAction) {
        nextAction(payload, id);
      }
    }

    this.setState(newState);
  };

  /**
   * cancel salesRoute
   */
  cancelSalesRoute = () => {
    // quotations
    const { actions } = this.props;
    const { salesroute } = this.state;
    actions.cancelSalesRoute(get(salesroute, 'id'));
  };

  /**
   * putOnHold salesRoute
   */
  putOnHoldSalesRoute = () => {
    // quotations
    const { actions } = this.props;
    const { salesroute } = this.state;
    actions.putOnHoldSalesRoute({ id: get(salesroute, 'id') });
  };

  editField = panelCode => () => {
    const { hasDirtyField } = this.state;
    const newDirtyField = { ...hasDirtyField };
    newDirtyField[panelCode] = true;

    this.setState({ hasDirtyField: newDirtyField });
  };

  onResetForm = () => {
    const { actions } = this.props;

    this.setState({
      willResetForm: {},
    });

    // Call this action to update the advancementStatus (by a reducer)
    actions.resetFormSalesRouteSuccess();
  };

  onSaveForm = () => {
    this.setState({
      willSaveForm: {},
      hasDirtyField: {},
    });
  };

  showAlerts = () => {
    const { enqueueSnackbar, alerts } = this.props;
    if (enqueueSnackbar && alerts) {
      const { strings } = this.props;
      showAlerts({ enqueueSnackbar, alerts, strings });
    }
  };

  updateSalesRouteId = id => {
    this.setState({
      salesroute: {
        id,
      },
    });
    this.fetchSalesRoute(id);
  };

  handleChangePanel = panel => {
    try {
      const { hasDirtyField, salesroute, steps } = this.state;
      const { advancement } = this.props;

      let currentPanel = find(steps, { state: STATE_MENU.CURRENT });
      let hasExpanded;

      if (!currentPanel) {
        currentPanel = find(steps, { expanded: true });
        hasExpanded = !!currentPanel;
      } else {
        hasExpanded = currentPanel.expanded;
      }

      // If block have been modified
      if (currentPanel && hasDirtyField[currentPanel.code] && (!panel.expanded || hasExpanded)) {
        let panelId = panel.id;

        if (!panelId) {
          panelId = find(steps, { code: panel.code })?.id;
        }

        if (currentPanel.id === panelId) {
          panelId = '__none__';
        }

        this.setState({ steps, showModal: true, nextEditBlock: panelId });
      } else {
        // Else block has not been modified
        let currentIdQuestionnaire = get(salesroute, 'questionnaires[0].questionnaire.id', undefined);

        const newDirtyField = { ...hasDirtyField };
        newDirtyField[panel.code] = false;

        this.setState({
          // Will update steps with panel
          steps: calculateSteps({
            advancement,
            steps,
            idQuestionnaire: currentIdQuestionnaire,
            panel,
          }),
          hasDirtyField: newDirtyField,
          nextEditBlock: null,
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`handleChangePanel ${e.stack}`);
    }
    return undefined;
  };

  handleChangeMenu = panel => {
    try {
      if (panel) {
        const { steps } = this.state;
        const step = find(steps, { id: panel.id });

        return this.handleChangePanel({ ...step, expanded: true, state: panel.state });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(`handleChangeMenu ${e.stack}`);
    }
    return undefined;
  };

  handleCancelModal() {
    const { actions } = this.props;
    let { steps, willResetForm } = this.state;

    let currentPanel = find(steps, { state: STATE_MENU.CURRENT });

    if (!currentPanel) {
      currentPanel = find(steps, { expanded: true });
    }

    steps = steps.map(step => {
      return { ...step, expanded: false };
    });

    const newResetForm = { ...willResetForm };
    newResetForm[currentPanel.code] = true;

    this.setState({
      showModal: false,
      willResetForm: newResetForm,
      steps,
      hasDirtyField: {},
    });

    actions.resetFormSalesRoute();
  }

  handleCloseModal() {
    let { steps } = this.state;

    steps = steps.map(step => {
      if (step.expanded && step.state !== STATE_MENU.CURRENT) {
        return { ...step, state: STATE_MENU.CURRENT };
      }
      if (!step.expanded && step.state === STATE_MENU.CURRENT) {
        return { ...step, state: STATE_MENU.ENABLED };
      }

      return step;
    });

    this.setState({ steps, showModal: false, nextEditBlock: null });
  }

  handleSaveModal() {
    const { steps, willSaveForm } = this.state;

    let currentPanel = find(steps, { state: STATE_MENU.CURRENT });

    if (!currentPanel) {
      currentPanel = find(steps, { expanded: true });
    }

    const newSaveForm = { ...willSaveForm };
    newSaveForm[currentPanel.code] = true;

    this.setState({
      showModal: false,
      willSaveForm: newSaveForm,
    });
  }

  // Don't save if form is not valid (missing field...) and display an error tooltip
  noSaveNotValid = () => {
    const { actions } = this.props;
    actions.noUpdateSalesRoute({ error: 'editsalesroute.error.message.formnotvalid' });

    this.setState({
      willSaveForm: {},
    });
  };

  eventChangePersonType = newVal => {
    const { actions } = this.props;
    const { salesroute } = this.state;

    actions.changePersonType({ id: get(salesroute, 'id'), value: newVal });
  };

  render() {
    const { pageTitle, canonical, strings, loading, advancement, i18n } = this.props;
    const {
      salesroute,
      steps,
      questionnaire,
      blockQuestionnaire,
      blockLoans,
      blockQuotation,
      blockExtraData,
      blockGeneralData,
      blockOffers,
      blockSubscription,
      blockDocuments,
      showModal,
      willResetForm,
      willSaveForm,
      displaySubscrDialog,
    } = this.state;
    const { title } = questionnaire;

    const helmet = (
      <Helmet>
        <meta charSet="utf-8" />
        <title>{strings[pageTitle] || ''}</title>
        <meta name="description" content={strings[pageTitle] || ''} />
        <link rel="canonical" href={canonical} />
      </Helmet>
    );

    const circularProgress = (
      <CircularProgress id="salesroute-loading" className="loading" scale={2} style={{ margin: '2em auto' }} />
    );

    //
    const isReadyQuestionnaireBlock = !isEmpty(blockQuestionnaire);
    const isReadyLoansBlock = !isEmpty(blockLoans);
    const isReadyQuotationBlock = !isEmpty(blockQuotation);
    const isReadyExtraDataBlock = !isEmpty(blockExtraData);
    const isReadyGeneralDataBlock = !isEmpty(blockGeneralData);
    const isReadyOffersBlock = !isEmpty(blockOffers);
    const isReadySubscriptionBlock = !isEmpty(blockSubscription);
    const isReadyDocumentsBlock = !isEmpty(blockDocuments);
    //
    const isFinishedSalesRoute = checkSalesRouteIsFinished({ salesroute });
    const { statusSR, headerButtons } = calculateHeaderState({ status: salesroute.status, strings });
    let subscribeDialog;

    const evtClickButton = id => {
      switch (id) {
        case 0:
          this.putOnHoldSalesRoute();
          break;
        case 1:
          this.cancelSalesRoute();
          break;
        default:
          break;
      }
    };

    if (displaySubscrDialog) {
      subscribeDialog = (
        <SubscribeDialog
          title={get(strings, 'editsalesroute.subscription.dialog.title', '')}
          buttonCloseLabel={get(strings, 'editsalesroute.subscription.dialog.button.close', 'Fermer')}
        >
          <>
            <div className="icon">{/* <FontIcon inherit>done_outline</FontIcon> */}</div>
            <div className="content">
              <span>
                {get(
                  strings,
                  'editsalesroute.subscription.dialog.text',
                  "Demande d'adhésion transmise au service gestion"
                )}
              </span>
            </div>
          </>
        </SubscribeDialog>
      );
    }

    return (
      <div className="coog-app salesroute">
        {subscribeDialog}
        {helmet}
        <Header title={title} status={statusSR} buttons={headerButtons} eventEmitter={evtClickButton} />
        <StepNavMenu data={steps} eventEmitter={this.handleChangeMenu} strings={strings} />
        <div className="md-body edit-salesroute">
          {!loading.get('isLoadingSalesRoute') ? (
            <>
              <div className="blocks">
                {steps.map(step => (
                  <ExpansionPanelBlock
                    objectExpansionPanel={{
                      title: get(step, 'label'),
                      code: get(step, 'code'),
                      expanded: get(step, 'expanded'),
                      disabled: get(step, 'state') === STATE_MENU.DISABLED,
                    }}
                    styleExpansionPanel={{ 'panel.backgroundColor': 'white' }}
                    eventEmitter={this.handleChangePanel}
                    key={get(step, 'code')}
                    strings={strings}
                  >
                    {/* Summary */}
                    <Summary
                      code={get(step, 'code', '')}
                      blockQuestionnaire={blockQuestionnaire}
                      blockLoans={blockLoans}
                      blockQuotation={blockQuotation}
                      blockExtraData={blockExtraData}
                      blockGeneralData={blockGeneralData}
                      blockOffers={blockOffers}
                      blockSubscription={blockSubscription}
                      blockDocuments={blockDocuments}
                      i18n={i18n}
                      strings={strings}
                    />
                    {/* DetailsBlock */}
                    {(() =>
                      BlocksPresentational({
                        codeStep: get(step, 'code'),
                        // check if block are ready to use
                        isReadyQuestionnaireBlock,
                        isReadyLoansBlock,
                        isReadyQuotationBlock,
                        isReadyExtraDataBlock,
                        isReadyGeneralDataBlock,
                        isReadyOffersBlock,
                        isReadySubscriptionBlock,
                        isReadyDocumentsBlock,
                        // check if sales route are finished
                        isFinishedSalesRoute,
                        // json schema for blocks
                        blockQuestionnaire,
                        blockLoans,
                        blockQuotation,
                        blockExtraData,
                        blockGeneralData,
                        blockOffers,
                        blockSubscription,
                        blockDocuments,
                        //
                        contracts: get(salesroute, 'contracts'),
                        advancementStatus: advancement.get('status'),
                        //
                        partialUpdateSalesRouteAnswers: this.partialUpdateSalesRoute('questionnaires'),
                        partialUpdateSalesRouteLoans: this.partialUpdateSalesRoute('loans'),
                        partialUpdateSalesRouteQuotations: this.partialUpdateSalesRoute('quotations'),
                        partialUpdateSalesRouteExtraData: this.partialUpdateSalesRoute('extra_data'),
                        partialUpdateSalesRouteGeneralData: this.partialUpdateSalesRoute('generalData'),
                        partialUpdateSalesRouteContract: this.partialUpdateSalesRoute('offers'),
                        partialUpdateSalesRouteContractPrint: this.partialUpdateSalesRoute('print'),
                        partialUpdateSalesRouteSubscription: this.partialUpdateSalesRoute('subscription'),
                        partialUpdateSalesRouteDocuments: this.partialUpdateSalesRoute('documents'),
                        //
                        isLoadingQuestionnaireBlock: loading.get('isLoadingQuestionnaireBlock'),
                        isLoadingLoansBlock: loading.get('isLoadingLoansBlock'),
                        isLoadingQuotationBlock: loading.get('isLoadingQuotationBlock'),
                        isLoadingExtraDataBlock: loading.get('isLoadingExtraDataBlock'),
                        isLoadingGeneralDataBlock: loading.get('isLoadingGeneralDataBlock'),
                        isLoadingOffersBlock: loading.get('isLoadingOffersBlock'),
                        isLoadingSubscriptionBlock: loading.get('isLoadingSubscriptionBlock'),
                        isLoadingDocumentsBlock: loading.get('isLoadingDocumentsBlock'),
                        //
                        cancelSalesRoute: this.cancelSalesRoute,
                        putOnHoldSalesRoute: this.putOnHoldSalesRoute,
                        strings,
                        circularProgress,
                        editField: this.editField(get(step, 'code')),
                        willResetForm,
                        onResetForm: this.onResetForm,
                        willSaveForm,
                        onSaveForm: this.onSaveForm,
                        noSaveNotValid: this.noSaveNotValid,
                        eventChangePersonType: this.eventChangePersonType,
                        i18n: i18n.toJS(),
                      }))()}
                  </ExpansionPanelBlock>
                ))}
              </div>
              <Modal
                isOpen={showModal}
                contentLabel="Edited Fields Modal"
                className="editedFieldsModal"
                overlayClassName="overlayModal"
              >
                <p className="text">
                  {get(
                    strings,
                    'editsalesroute.action.modal.editedfields.label',
                    'Des modifications ont été apportées, que souhaitez-vous faire ?'
                  )}
                </p>
                <button type="button" className="cancel" onClick={this.handleCloseModal}>
                  {get(strings, 'editsalesroute.action.button.cancel.label', 'Annuler')}
                </button>
                <button type="button" className="notsave" onClick={this.handleCancelModal}>
                  {get(strings, 'editsalesroute.action.button.notsave.label', 'Ne pas sauvegarder')}
                </button>
                <button type="button" className="save" onClick={this.handleSaveModal}>
                  {get(strings, 'editsalesroute.action.button.save.label', 'Sauvegarder')}
                </button>
              </Modal>
            </>
          ) : (
            <CircularProgress id="salesroute-loading" className="loading" scale={3} style={{ margin: '2em auto' }} />
          )}
        </div>
      </div>
    );
  }
}

EditSalesRoute.propTypes = {
  pageTitle: PropTypes.string,
  canonical: PropTypes.string,
  strings: PropTypes.objectOf(PropTypes.string),
  actions: PropTypes.shape({
    fetchSalesRoute: PropTypes.func,
    fetchQuestionnaire: PropTypes.func,
    fetchQuestionnaires: PropTypes.func,
    partialUpdateSalesRouteAnswers: PropTypes.func,
    partialUpdateSalesRouteLoans: PropTypes.func,
    partialUpdateSalesRouteQuotations: PropTypes.func,
    partialUpdateSalesRouteExtraData: PropTypes.func,
    partialUpdateSalesRouteGeneralData: PropTypes.func,
    partialUpdateSalesRouteContract: PropTypes.func,
    cancelSalesRoute: PropTypes.func,
    putOnHoldSalesRoute: PropTypes.func,
  }),
  match: ReactRouterPropTypes.match.isRequired,
  salesroutes: PropTypes.object, // eslint-disable-line
  questionnaires: PropTypes.object, // eslint-disable-line
  blockQuestionnaire: PropTypes.object, // eslint-disable-line
  blockLoans: PropTypes.object, // eslint-disable-line
  blockOffers: PropTypes.object, // eslint-disable-line
  blockQuotation: PropTypes.object, // eslint-disable-line
  blockExtraData: PropTypes.object, // eslint-disable-line
  blockGeneralData: PropTypes.object, // eslint-disable-line
  blockSubscription: PropTypes.object, // eslint-disable-line
  blockDocuments: PropTypes.object, // eslint-disable-line
  i18n: PropTypes.object, // eslint-disable-line
  loading: PropTypes.object, // eslint-disable-line
  advancement: PropTypes.object, // eslint-disable-line
  enqueueSnackbar: PropTypes.func.isRequired, // eslint-disable-line
  alerts: PropTypes.object, // eslint-disable-line
  // products: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

EditSalesRoute.defaultProps = {
  pageTitle: 'title.salesroute',
  canonical: 'http://www.coopengo.com/',
  strings: {},
  actions: {
    fetchSalesRoute: () => {},
    fetchQuestionnaire: () => {},
    fetchQuestionnaires: () => {},
    partialUpdateSalesRouteAnswers: () => {},
    partialUpdateSalesRouteLoans: () => {},
    partialUpdateSalesRouteQuotations: () => {},
    partialUpdateSalesRouteExtraData: () => {},
    partialUpdateSalesRouteGeneralData: () => {},
    partialUpdateSalesRouteContract: () => {},
    cancelSalesRoute: () => {},
    putOnHoldSalesRoute: () => {},
  },
  salesroutes: fromJS({}),
  questionnaires: fromJS({}),
  blockQuestionnaire: fromJS({}),
  blockLoans: fromJS({}),
  blockOffers: fromJS({}),
  blockQuotation: fromJS({}),
  blockExtraData: fromJS({}),
  blockGeneralData: fromJS({}),
  blockSubscription: fromJS({}),
  blockDocuments: fromJS({}),
  i18n: fromJS({}),
  loading: fromJS({
    isLoadingSalesRoutes: false,
    isLoadingSalesRoute: false,
    isCreatingSalesRoute: false,
    isDeletingSalesRoute: false,
    isUpdatingSalesRoute: false,
    isLoadingQuestionnaires: false,
    //
    isLoadingQuestionnaireBlock: false,
    isLoadingLoansBlock: false,
    isLoadingQuotationBlock: false,
    isLoadingExtraDataBlock: false,
    isLoadingGeneralDataBlock: false,
    isLoadingSubscriptionBlock: false,
    isLoadingDocumentsBlock: false,
  }),
  advancement: fromJS({
    status: undefined,
  }),
  alerts: fromJS({ warnings: [], errors: [], successes: [], infos: [] }),
  // products: fromJS([]),
};
// eslint-disable-next-line no-unused-vars
const mapStateToProps = state => ({
  alerts: state.getIn(['salesroutes', 'alerts']),
  strings: state.getIn(['salesroutes']).toJS().strings,
  salesroutes: state.getIn(['salesroutes', 'list']),
  loading: state.getIn(['salesroutes', 'loading']),
  advancement: state.getIn(['salesroutes', 'advancement']),
  i18n: state.getIn(['system', 'i18n']),
  questionnaires: state.getIn(['salesroutes', 'questionnaires']),
  blockQuestionnaire: state.getIn(['salesroutes', 'blockQuestionnaire']),
  blockLoans: state.getIn(['salesroutes', 'blockLoans']),
  blockExtraData: state.getIn(['salesroutes', 'blockExtraData']),
  blockGeneralData: state.getIn(['salesroutes', 'blockGeneralData']),
  blockOffers: state.getIn(['salesroutes', 'blockOffers']),
  blockQuotation: state.getIn(['salesroutes', 'blockQuotation']),
  blockSubscription: state.getIn(['salesroutes', 'blockSubscription']),
  blockDocuments: state.getIn(['salesroutes', 'blockDocuments']),
  products: state.getIn(['salesroutes', 'products']),
});
// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...salesroutesActions }, dispatch),
});

export default withSnackbar(withRouter(connect(mapStateToProps, mapDispatchToProps)(EditSalesRoute)));
